import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api, useAuth } from '../context/AuthContext';
import { Heart, X, ChevronRight, TrendingUp, Tag } from 'lucide-react';

export default function WishlistSidebar() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user || !open) return;
    setLoading(true);
    api.get('/wishlist').then(({ data }) => setItems(data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [user, open]);

  const remove = async (id, e) => {
    e.preventDefault();
    await api.delete(`/wishlist/${id}`);
    setItems(p => p.filter(x => x.id !== id));
  };

  if (!user) return null;

  return (
    <>
      {/* Toggle button */}
      <button
        onClick={() => setOpen(o => !o)}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-40 bg-primary text-white flex flex-col items-center gap-1 py-3 px-2 rounded-l-xl shadow-lg hover:bg-primary-dark transition"
        title="Favoritos"
      >
        <Heart className="w-5 h-5" />
        {items.length > 0 && (
          <span className="text-xs font-bold">{items.length}</span>
        )}
        <ChevronRight className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Sidebar panel */}
      <div className={`fixed right-0 top-0 h-full w-72 bg-white shadow-2xl z-50 flex flex-col transition-transform duration-300 ${open ? 'translate-x-0' : 'translate-x-full'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b bg-primary text-white">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 fill-white" />
            <span className="font-bold">Mis Favoritos</span>
            <span className="bg-white/20 text-xs px-2 py-0.5 rounded-full">{items.length}</span>
          </div>
          <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-10">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Heart className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No tienes favoritos aún</p>
              <Link to="/products" onClick={() => setOpen(false)} className="text-primary text-sm font-semibold hover:underline mt-2 block">
                Explorar productos →
              </Link>
            </div>
          ) : (
            <div className="divide-y">
              {items.map(p => (
                <Link key={p.id} to={`/products/${p.id}`} onClick={() => setOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition group">
                  <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 shrink-0">
                    {p.images?.[0]?.url
                      ? <img src={p.images[0].url} alt={p.title} className="w-full h-full object-cover" />
                      : <span className="w-full h-full flex items-center justify-center text-2xl">🛞</span>
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{p.title}</p>
                    <p className="text-primary font-bold text-sm">₡{p.price?.toLocaleString('es-CR')}</p>
                  </div>
                  <button onClick={e => remove(p.id, e)}
                    className="opacity-0 group-hover:opacity-100 transition p-1 hover:text-red-500">
                    <X className="w-4 h-4" />
                  </button>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t p-4 space-y-4">
            {/* Summary */}
            <h2 className="font-bold text-lg">Resumen</h2>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
                <Heart className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Total guardados</p>
                <p className="font-bold text-lg">{items.length}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Valor total</p>
                <p className="font-bold text-lg">₡{items.reduce((s,p)=>s+(p.price||0),0).toLocaleString('es-CR')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                <Tag className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Precio promedio</p>
                <p className="font-bold text-lg">₡{Math.round(items.reduce((s,p)=>s+(p.price||0),0)/items.length).toLocaleString('es-CR')}</p>
              </div>
            </div>
            <Link to="/wishlist" onClick={() => setOpen(false)}
              className="block w-full text-center bg-primary text-white py-2 rounded-full font-semibold text-sm hover:bg-primary-dark transition">
              Ver todos los favoritos
            </Link>
          </div>
        )}
      </div>

      {/* Backdrop */}
      {open && <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setOpen(false)} />}
    </>
  );
}
